import { fx } from '../utilities/animation-preferences';
import { breakpoint } from '../utilities/variables';
import { getHeaderHeight } from '../utilities/functions';

class HeaderNavigation {
    constructor() {
        // Sélection des éléments
        this.nav = document.getElementById('header-navigation');
        this.menu = this.nav?.querySelector('.header-menu');
        this.items = this.menu ? Array.from(this.menu.children) : [];
        this.header = document.getElementById('header');
        this.burger = document.querySelector('.header-burger');

        // Bind des méthodes
        this.onOpen = this.onOpen.bind(this);
        this.onClose = this.onClose.bind(this);
        this.onResize = this.onResize.bind(this);
        this.onLinkClick = this.onLinkClick.bind(this);
    }

    init() {
        if (!this.nav) return;

        // Écoute des événements personnalisés émis par tools.js
        this.nav.addEventListener('selfOpened', this.onOpen);
        this.nav.addEventListener('selfClosed', this.onClose);

        // Liens (ancres) du menu – même comportement mobile/desktop
        const links = this.nav.querySelectorAll('.menu-link[href^="#"]');
        links.forEach((a) => a.addEventListener('click', this.onLinkClick));

        // Gestion du resize
        window.addEventListener('resizeEnd', this.onResize);
    }

    isMobile() {
        return window.innerWidth < breakpoint.desktop;
    }

    onOpen() {
        if (!this.isMobile()) return;
        if (!this.items.length) return;

        // Préparation des styles inline avant l'animation
        fx.set(this.nav, { opacity: 0 });
        fx.set(this.items, { opacity: 0, y: -24, willChange: 'opacity, transform' });

        // Animation des items lors de l'ouverture mobile
        // Subtile: fade + translateY, avec remise à zéro des styles inline à la fin
        const tl = fx.timeline();

        // Animer le nav
        tl.to(this.nav, { opacity: 1, duration: 0.2 });

        // Animer ensuite les items
        tl.to(this.items, {
            opacity: 1,
            y: 0,
            duration: 0.6,
            stagger: 0.06,
            ease: 'back.out(1.7)',
            onComplete: () => this.resetInlineOnItems(),
        });
    }

    onClose() {
        // En fermeture, on s'assure de nettoyer les styles résiduels
        this.resetInlineOnItems();
    }

    onResize() {
        // Si on passe en desktop, on reset l'état JS pour laisser le CSS prendre la main
        if (!this.nav) return;
        if (window.innerWidth >= breakpoint.desktop) {
            // Nettoyage des styles inline sur le nav et ses items
            this.resetInlineOnNav();
            this.resetInlineOnItems();
            // Forcer le bouton burger en état "fermé"
            if (this.burger) this.burger.setAttribute('aria-expanded', 'false');
        }
    }

    async onLinkClick(e) {
        const href = e.currentTarget.getAttribute('href');
        if (!href || href.charAt(0) !== '#') return;
        const target = document.querySelector(href);
        if (!target) return;

        e.preventDefault();

        const headerH = this.header ? this.header.offsetHeight : 0;
        const targetTop = Math.max(0, this.getElementPageY(target) - headerH);

        // Smooth scroll natif
        window.scrollTo({ top: targetTop, behavior: 'smooth' });
        await this.waitForScrollEnd();

        // Sur mobile, on ferme le menu après le scroll
        if (this.isMobile()) {
            // Masquer le nav (cohérent avec tools.js qui gère display/hidden)
            this.nav.style.display = 'none';
            this.nav.hidden = true;
            if (this.burger) this.burger.setAttribute('aria-expanded', 'false');
        }
    }

    // ------- Utils -------
    resetInlineOnItems() {
        if (!this.items?.length) return;
        fx.set(this.items, fx.clearProps);
    }

    resetInlineOnNav() {
        // Nettoie les styles inline susceptibles d'avoir été posés (display, transform, etc.)
        this.nav.removeAttribute('style');
        this.nav.hidden = false; // en desktop, on laisse le CSS décider
    }

    getElementPageY(el) {
        const rect = el.getBoundingClientRect();
        return rect.top + window.pageYOffset;
    }

    waitForScrollEnd(timeout = 150) {
        return new Promise((resolve) => {
            let scrollTimer = null;
            const onScroll = () => {
                clearTimeout(scrollTimer);
                scrollTimer = setTimeout(() => {
                    window.removeEventListener('scroll', onScroll, { passive: true });
                    resolve();
                }, timeout);
            };
            window.addEventListener('scroll', onScroll, { passive: true });

            // Au cas où il y ait très peu de distance à parcourir
            onScroll();
        });
    }
}

export default {
    init() {
        const instance = new HeaderNavigation();
        instance.init();
    },
};
